import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiConfirmSettlementRequest } from 'api/rtk-api'

import Api from '@/api/_legacy'
import { ApiAccountApprovalEvent, ApiTradeActivation } from '@/api/_legacy/generated-api'
import { UserRole, UserType } from '@/types/user'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../../../tradebuilder-v2/constants'
import { TradeActivationStep } from '../../../../tradebuilder-v2/types'
import { Errors } from '../../../../tradebuilder-v2/util'
import { parseApproveErrors } from '../../../util/validator'

export type TradeActivateStepState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  canProceed: boolean
  errors: Errors
  generalErrors?: []
  hasCompleted: boolean
  success?: boolean
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

const initialState: TradeActivateStepState = {
  canProceed: true,
  errors: {},
  generalErrors: [],
  accountId: undefined,
  userName: undefined,
  userType: undefined,
  userRoles: [],
  approvalEvents: [],
  hasCompleted: false
}

export type TradeActivateFormState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  isSwitch: boolean
  status: TradeActivationStep
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export const tradeActivateSlice = createSlice({
  name: 'tradeActivateSlice',
  initialState,
  reducers: {
    reset: state => {
      state.canProceed = true
      state.hasCompleted = false
      state.success = undefined
      state.errors = {}
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    initialise: (state, action: PayloadAction<TradeActivateFormState>) => {
      state.accountId = action.payload.accountId
      state.userName = action.payload.userName
      state.userType = action.payload.userType
      state.userRoles = action.payload.userRoles
      state.approvalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(
            x =>
              x.statusBefore === 'PendingActivation' || x.statusBefore === 'PendingClientCommitment'
          )
        : []
      state.hasCompleted =
        action.payload.status > TradeActivationStep.activate ||
        (action.payload.isSwitch && action.payload.status > TradeActivationStep.settlement)
      state.success = false
    },
    postTradeApproveInProgress: state => {
      state.canProceed = false
    },
    postTradeApproveSuccess: state => {
      state.errors = {}
      state.success = true
      state.canProceed = true
    },
    postTradeApproveFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
      state.canProceed = true
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const actions = { ...tradeActivateSlice.actions }

export const postActivateApprove = (accountId: string, comments: string) => async dispatch => {
  try {
    dispatch(actions.postTradeApproveInProgress())
    const request: ApiTradeActivation = {
      comments
    }
    await Api.tenant.activateCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(actions.postTradeApproveSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

export const postActivateApproveSwitch = (accountId: string) => async dispatch => {
  try {
    dispatch(actions.postTradeApproveInProgress())
    const request: ApiConfirmSettlementRequest = {}
    await Api.tenant.confirmCreate(accountId, TENANT_ID, request, { format: 'json' })
    dispatch(actions.postTradeApproveSuccess())
  } catch (response) {
    const errors: Errors = parseApproveErrors(response)
    dispatch(actions.postTradeApproveFailure(errors))
  }
}

export type TradeActivateActions = typeof actions

export default tradeActivateSlice.reducer
